import { useState, useEffect, useCallback } from 'react'
import type { FullPokemonCard, FindCardResult } from '@/types/pokemon'
import { getApiUrl } from '@/lib/utils'

export function ImagesTab() {
  const [images, setImages] = useState<string[]>([])
  const [imageData, setImageData] = useState<Record<string, FullPokemonCard | null>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const API_URL = getApiUrl()

  const fetchImages = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${API_URL}/api/images`)

      if (!response.ok) {
        throw new Error('Failed to fetch images')
      }

      const imageList = await response.json()
      // Filter out any non-image files and sort by name (newest first)
      const imageFiles = imageList.filter((file: string) => /\.(jpg|jpeg|png|gif|webp)$/i.test(file)).sort((a: string, b: string) => b.localeCompare(a))

      setImages(imageFiles)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred')
    } finally {
      setLoading(false)
    }
  }, [API_URL])

  const fetchImageData = useCallback(
    async (imageName: string): Promise<FullPokemonCard | null> => {
      try {
        const response = await fetch(`${API_URL}/api/image/${imageName}.json`)
        if (response.ok) {
          const data = (await response.json()) as FindCardResult
          // Check if it's an error response
          if (!data.bestMatch) {
            return null
          }
          return data.bestMatch
        }
        return null
      } catch {
        return null
      }
    },
    [API_URL],
  )

  useEffect(() => {
    fetchImages()
  }, [fetchImages])

  useEffect(() => {
    // Fetch card data for each image
    const fetchAllImageData = async () => {
      const data: Record<string, FullPokemonCard | null> = {}
      await Promise.all(
        images.map(async (imageName) => {
          const cardData = await fetchImageData(imageName)
          data[imageName] = cardData
        }),
      )
      setImageData(data)
    }

    if (images.length > 0) {
      fetchAllImageData()
    }
  }, [images, fetchImageData])

  const getImageUrl = (imageName: string) => {
    return `${API_URL}/api/image/${imageName}`
  }

  const handleDownload = async (imageName: string) => {
    try {
      const imageUrl = getImageUrl(imageName)
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = imageName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download image')
    }
  }

  return (
    <div className="bg-white rounded-2xl p-6 md:p-8 min-h-[400px] border border-gray-200 w-full shadow-sm">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <h2 className="mt-0 mb-0 text-3xl text-gray-900">Upload History</h2>

        <button
          onClick={fetchImages}
          disabled={loading}
          className="py-3 px-6 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg font-semibold text-base transition-all duration-300 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <span className="inline-block w-4 h-4 border-2 border-blue-400/30 border-t-blue-600 rounded-full animate-spin mr-2"></span>
              Loading...
            </>
          ) : (
            '🔄 Refresh'
          )}
        </button>
      </div>

      {loading && (
        <div className="flex flex-col items-center gap-4 p-12">
          <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 m-0">Loading images...</p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 mb-6">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {!loading && !error && images.length === 0 && (
        <div className="text-center py-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-gray-500"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 m-0 mb-2">No images uploaded yet</h3>
          <p className="text-gray-500 text-base m-0">Upload images in the Scan Cards tab to see them here</p>
        </div>
      )}

      {!loading && !error && images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {images.map((imageName) => (
            <div key={imageName} className="flex flex-col gap-2">
              <div className="group relative aspect-square bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:border-blue-400 transition-all duration-300 hover:scale-105">
                <img src={getImageUrl(imageName)} alt={imageName} className="w-full h-full object-contain" loading="lazy" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDownload(imageName)
                    }}
                    className="relative z-10 flex items-center gap-2 py-2 px-4 rounded-lg font-semibold text-sm transition-all duration-200 hover:scale-105 shadow-lg"
                    style={{ backgroundColor: '#1E40AF', color: '#ffffff' }}
                    title="Download image"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Download
                  </button>
                </div>
              </div>
              <div className="text-gray-900 text-xs text-center break-words font-medium px-1">{`${imageData[imageName]?.set?.name} - ${imageData[imageName]?.name}`}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
